import React, { useState, useEffect, useMemo } from 'react';
import FilterBar from '../components/FilterBar';
import VerbatimFilter from '../components/VerbatimFilter';
import ChartGenerator from '../components/ChartGenerator';
import ChartRenderer from '../components/ChartRenderer';
import ChartModal from '../modals/ChartModal';
import VerbatimsModal from '../modals/VerbatimsModal';
import { useVerbatim } from '../context/VerbatimContext';
import { useSetup } from '../context/SetupContext';
import { exportChartDataToExcel } from '../utils/ExcelUtils'; // Import from utils

import './VisualizationPage.css';

const VisualizationPage = () => {
  const { getVerbatims } = useVerbatim(); // Use the getter function to access verbatims
  const { labelTypes } = useSetup(); // Label types (Gender, Age, etc.)
  const [filteredVerbatims, setFilteredVerbatims] = useState([]); // Filtered verbatims to display
  const [filters, setFilters] = useState({
    source: "",
    dynamicFilters: Object.keys(labelTypes).reduce((acc, labelType) => {
      acc[labelType] = [];
      return acc;
    }, {}),
  });
  const [showModal, setShowModal] = useState(false);
  const [modalVerbatims, setModalVerbatims] = useState([]);
  const [modalTitle, setModalTitle] = useState("");
  const [modalData, setModalData] = useState(null); // For enlarged chart modal
  const [isCollapsibleOpen, setIsCollapsibleOpen] = useState(false);
  const [showPercentages, setShowPercentages] = useState(false); // Toggle for percentages vs raw numbers
  const [showMixedSentiment, setShowMixedSentiment] = useState(false); // Toggle for merging negative and mixed sentiments

  // Toggle between percentages and raw numbers
  const toggleView = () => {
    setShowPercentages(!showPercentages);
  };

  // Update filtered verbatims when filters or verbatims change
  useEffect(() => {
    const verbatims = getVerbatims(); // Access verbatims from context
    const result = VerbatimFilter(verbatims, filters); // Filter the verbatims
    setFilteredVerbatims(result);
  }, [filters, getVerbatims]); // Dependencies ensure it runs when filters or verbatims change

  const toggleCollapsible = () => {
    setIsCollapsibleOpen(!isCollapsibleOpen);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleBarClick = (labelType, label, sentiment) => {
    const filtered = filteredVerbatims.filter((verbatim) => {
      // Use optional chaining to safely access labels
      const labels = verbatim.labels?.[labelType];
      // Ensure labels is an array
      if (!Array.isArray(labels)) return false;

      if (labelTypes[labelType]?.sentimentEnabled) {
        // A specific case "negative/mixed"
        if (sentiment.toLowerCase() === 'negative/mixed') {
          return labels.some(
            (l) => l && (l.name === label || l.name === 'negative' || l.name === 'mixed') && (l.state === 'negative' || l.state === 'mixed')
          );
        }
        // Standard case
        return labels.some(
          (l) => l && l.name === label && l.state === sentiment.toLowerCase()
        );
      } else {
        // Ensure l is not null
        return labels.some((l) => l && l.name === label);
      }
    });

    setModalTitle(`${label} ${sentiment ? ` with sentiment ${sentiment}` : ''}`);
    setModalVerbatims(filtered);
    setShowModal(true);
  };

  // Handle enlarge button click
  const handleEnlargeClick = (chartData) => {
    setModalData(chartData); // Open modal with chart data
  };

  const handleModalClose = () => {
    setModalData(null); // Close chart modal
  };

  const handleExportClick = (labelType) => {
    // Ensure chartData and chartData[labelType] are defined
    if (chartData && chartData[labelType]) {
      exportChartDataToExcel(labelType, chartData, sampleSizes, labelTypes, filters, showPercentages, showMixedSentiment);
    } else {
      console.error('Chart data is not available for export');
    }
  }

  // Memoize chartData and sampleSizes to prevent infinite re-renders
  const { chartData, sampleSizes } = useMemo(() => {
    return ChartGenerator({
      filteredVerbatims,
      labelTypes,
      asPercentages: showPercentages, // Pass the toggle state to control raw numbers or percentages
      asMixedSentiment: showMixedSentiment, 
    });
  }, [filteredVerbatims, labelTypes, showPercentages, showMixedSentiment]); // Include showPercentages in dependencies

  return (
    <div className="visualization-page">
      <div className="filter-bar-section">
        <div>
          <div className="collapsible-container">
            <button className="collapsible-toggle" onClick={toggleCollapsible}>
              {/* Conditionally render the icons based on isCollapsibleOpen */}
              {isCollapsibleOpen 
                ? (<i className="fa-solid fa-xmark menu-icon"></i>) 
                : (<i className="fa-solid fa-bars menu-icon"></i>)
              }
              {isCollapsibleOpen ? 'Hide Menu' : 'Show Menu'}
            </button>
            <div className="collapsible-content" style={{ display: isCollapsibleOpen ? 'block' : 'none' }}>
              <div className="option-container">
                <label className="toggle-switch">
                  <input type="checkbox" checked={showPercentages} onChange={toggleView} />
                  <span className="slider"></span>
                </label>
                <span className="option-text">{showPercentages ? 'As %' : 'As %'}</span>
              </div>
            </div>
            <div className="collapsible-content" style={{ display: isCollapsibleOpen ? 'block' : 'none' }}>
              <div className="option-container">
                <label className="toggle-switch">
                  <input type="checkbox" checked={showMixedSentiment} onChange={() => setShowMixedSentiment(!showMixedSentiment)} />
                  <span className="slider"></span>
                </label>
                <span className="option-text">Merge neg. & mixed</span>
              </div>
            </div>
          </div>
        </div>
        <div className="filter-section">
          <FilterBar onFilterChange={handleFilterChange} />
        </div>
      </div>

      <div className="charts-section">
        {Object.keys(chartData).map((labelType) => (
          <div className="chart-card" key={labelType}>
            <div className="chart-container">
              <h3>{labelType} (Sample: {sampleSizes[labelType] || 0})</h3>
              {/* Container for icon buttons */}
              <div className="icon-buttons-container">
                {/* Zoom button */}
                <button
                  className="icon-button zoom-button" onClick={() => handleEnlargeClick({ type: labelType, data: chartData[labelType], sampleSizes, labelTypes })} title="Zoom In">
                  <i className="fa-solid fa-magnifying-glass"></i>
                </button>
                {/* Export to Excel button */}
                <button className="icon-button export-button" onClick={() => handleExportClick(labelType)} title="Export Data">
                  <i className="fa-solid fa-download"></i>
                </button>
              </div>
              {/* Render the chart */}
              <ChartRenderer
                labelType={labelType}
                chartData={chartData}
                sampleSizes={sampleSizes}
                labelTypes={labelTypes}
                asMixedSentiment={showMixedSentiment}
                asPercentages={showPercentages}
                onBarClick={handleBarClick}
              />
            </div>
          </div>
        ))}
      </div>
      {showModal && (<VerbatimsModal title={modalTitle} verbatims={modalVerbatims} onClose={() => setShowModal(false)} />)}
      {modalData && (<ChartModal chartData={modalData} onClose={handleModalClose} handleBarClick={handleBarClick} />)}
    </div>
  );
};

export default VisualizationPage;
